// Opens the link the app loaded at (/parlays/<id>, /notifications, and /settings and /admin from emails
// and redirects), and a notification card's parlay. The URL then goes back to /.

import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import type { ShareWithMe } from '@parlay-picker/shared/hooks';
import {
  NOTIFICATION_LINK,
  type NotificationLink,
  parseNotificationLink,
} from '@parlay-picker/shared/notificationLinks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from '../config/api';
import type { TabId } from '../types/tabs';
import { DEFAULT_FILTERS, serverFilterParams } from './tracked-parlays/helpers';
import type { UseTrackedParlaysResult } from './tracked-parlays/types';

// =============================================================================
// Deciding what a parlay link opens
// =============================================================================

/** What the server says the viewer may see of a linked parlay. */
export const PARLAY_ACCESS = {
  OWN: 'own',
  SHARED: 'shared',
  NONE: 'none',
  FAILED: 'failed',
} as const;
export type ParlayAccess = (typeof PARLAY_ACCESS)[keyof typeof PARLAY_ACCESS];

export const PARLAY_LINK_STEP = {
  WAIT: 'wait',
  RESET_FILTERS: 'reset_filters',
  LOAD_MORE: 'load_more',
  OPEN_OWN: 'open_own',
  OPEN_SHARED: 'open_shared',
  UNAVAILABLE: 'unavailable',
  FAILED: 'failed',
} as const;
export type ParlayLinkStep = (typeof PARLAY_LINK_STEP)[keyof typeof PARLAY_LINK_STEP];

export interface ParlayLinkProgress {
  parlayId: number;
  /** null until the lookup answers */
  access: ParlayAccess | null;
  myBets: Pick<UseTrackedParlaysResult, 'trackedParlays' | 'loading' | 'loadingMore' | 'error'> & {
    hasMore: boolean;
    /** A status, source or book filter is on, so the server may be leaving the parlay out */
    filtersNarrowed: boolean;
  };
}

export const PARLAY_UNAVAILABLE_MESSAGE = "That parlay isn't available.";
export const PARLAY_LINK_FAILED_MESSAGE = "Couldn't open that parlay. Try again.";

export function nextParlayLinkStep({
  parlayId,
  access,
  myBets,
}: ParlayLinkProgress): ParlayLinkStep {
  if (myBets.trackedParlays.some((parlay) => parlay.id === parlayId)) {
    return PARLAY_LINK_STEP.OPEN_OWN;
  }
  switch (access) {
    case null:
      return PARLAY_LINK_STEP.WAIT;
    case PARLAY_ACCESS.FAILED:
      return PARLAY_LINK_STEP.FAILED;
    case PARLAY_ACCESS.SHARED:
      return PARLAY_LINK_STEP.OPEN_SHARED;
    case PARLAY_ACCESS.NONE:
      return PARLAY_LINK_STEP.UNAVAILABLE;
    case PARLAY_ACCESS.OWN:
      if (myBets.error) return PARLAY_LINK_STEP.FAILED;
      if (myBets.loading || myBets.loadingMore) return PARLAY_LINK_STEP.WAIT;
      if (myBets.filtersNarrowed) return PARLAY_LINK_STEP.RESET_FILTERS;
      return myBets.hasMore ? PARLAY_LINK_STEP.LOAD_MORE : PARLAY_LINK_STEP.UNAVAILABLE;
  }
}

type AuthFetch = ReturnType<typeof useAuth>['authFetch'];

/** GET /api/parlays/:id answers 404 for any parlay that is not the viewer's. */
export async function lookUpParlayAccess(
  authFetch: AuthFetch,
  parlayId: number,
): Promise<ParlayAccess> {
  try {
    const own = await authFetch(apiUrl(`/api/parlays/${parlayId}`));
    if (own.ok) return PARLAY_ACCESS.OWN;
    if (own.status !== 404) return PARLAY_ACCESS.FAILED;

    const received = await authFetch(apiUrl('/api/shares/shared-with-me'));
    if (!received.ok) return PARLAY_ACCESS.FAILED;
    const { shares } = (await received.json()) as { shares?: ShareWithMe[] };
    return (shares ?? []).some((share) => share.parlayId === parlayId)
      ? PARLAY_ACCESS.SHARED
      : PARLAY_ACCESS.NONE;
  } catch {
    return PARLAY_ACCESS.FAILED;
  }
}

// =============================================================================
// Hook
// =============================================================================

interface UseNotificationLinkOptions {
  myBets: Pick<
    UseTrackedParlaysResult,
    | 'trackedParlays'
    | 'loading'
    | 'loadingMore'
    | 'error'
    | 'pagination'
    | 'loadMore'
    | 'filters'
    | 'setFilters'
  >;
  setActiveTab: (tab: TabId) => void;
  openSettings: () => void;
  isAdmin: boolean;
}

export interface UseNotificationLinkResult {
  /** Opens a parlay by the same rule as a /parlays/<id> link, for a notification card's tap */
  openParlay: (parlayId: number) => void;
  /** The parlay My Bets should expand and scroll to, until it calls onOpenParlayHandled */
  openParlayId: number | null;
  onOpenParlayHandled: () => void;
  /** The shared parlay the Activity view should show, until it calls onLinkedShareHandled */
  linkedSharedParlayId: number | null;
  onLinkedShareHandled: () => void;
}

// Keeps the query: SettingsView reads its own ?connect= when it opens
function clearLinkFromUrl(): void {
  window.history.replaceState(window.history.state, '', `/${window.location.search}`);
}

export function useNotificationLink({
  myBets,
  setActiveTab,
  openSettings,
  isAdmin,
}: UseNotificationLinkOptions): UseNotificationLinkResult {
  const { authFetch } = useAuth();
  const { showToast } = useToast();
  const [link] = useState<NotificationLink | null>(() =>
    parseNotificationLink(window.location.pathname),
  );
  const [pendingParlayId, setPendingParlayId] = useState<number | null>(null);
  // Keyed by parlay, so a slow answer for an earlier tap cannot decide a later one
  const [lookup, setLookup] = useState<{ parlayId: number; access: ParlayAccess } | null>(null);
  const [openParlayId, setOpenParlayId] = useState<number | null>(null);
  const [linkedSharedParlayId, setLinkedSharedParlayId] = useState<number | null>(null);

  const openParlay = useCallback(
    (parlayId: number) => {
      setActiveTab('parlays');
      setPendingParlayId(parlayId);
      lookUpParlayAccess(authFetch, parlayId).then((access) => setLookup({ parlayId, access }));
    },
    [authFetch, setActiveTab],
  );

  // Once per page load: StrictMode runs mount effects twice, and a second lookup is waste
  const startedRef = useRef(false);
  useEffect(() => {
    if (!link || startedRef.current) return;
    startedRef.current = true;
    switch (link.kind) {
      case NOTIFICATION_LINK.PARLAY:
        openParlay(link.parlayId);
        return;
      case NOTIFICATION_LINK.NOTIFICATIONS:
        setActiveTab('feed');
        break;
      case NOTIFICATION_LINK.SETTINGS:
        openSettings();
        break;
      case NOTIFICATION_LINK.ADMIN:
        if (isAdmin) setActiveTab('admin');
        break;
    }
    clearLinkFromUrl();
  }, [link, openParlay, setActiveTab, openSettings, isAdmin]);

  const { trackedParlays, loading, loadingMore, error, pagination, loadMore, filters, setFilters } =
    myBets;
  useEffect(() => {
    if (pendingParlayId === null) return;
    const step = nextParlayLinkStep({
      parlayId: pendingParlayId,
      access: lookup?.parlayId === pendingParlayId ? lookup.access : null,
      myBets: {
        trackedParlays,
        loading,
        loadingMore,
        error,
        hasMore: pagination.hasMore,
        filtersNarrowed: Object.keys(serverFilterParams(filters)).length > 0,
      },
    });
    switch (step) {
      case PARLAY_LINK_STEP.WAIT:
        return;
      case PARLAY_LINK_STEP.RESET_FILTERS:
        setFilters(DEFAULT_FILTERS);
        return;
      case PARLAY_LINK_STEP.LOAD_MORE:
        loadMore();
        return;
      case PARLAY_LINK_STEP.OPEN_OWN:
        setOpenParlayId(pendingParlayId);
        break;
      case PARLAY_LINK_STEP.OPEN_SHARED:
        setActiveTab('feed');
        setLinkedSharedParlayId(pendingParlayId);
        break;
      case PARLAY_LINK_STEP.UNAVAILABLE:
        showToast(PARLAY_UNAVAILABLE_MESSAGE, 'warning');
        break;
      case PARLAY_LINK_STEP.FAILED:
        showToast(PARLAY_LINK_FAILED_MESSAGE, 'error');
        break;
    }
    setPendingParlayId(null);
    clearLinkFromUrl();
  }, [
    pendingParlayId,
    lookup,
    trackedParlays,
    loading,
    loadingMore,
    error,
    pagination.hasMore,
    loadMore,
    filters,
    setFilters,
    setActiveTab,
    showToast,
  ]);

  const onOpenParlayHandled = useCallback(() => setOpenParlayId(null), []);
  const onLinkedShareHandled = useCallback(() => setLinkedSharedParlayId(null), []);

  return {
    openParlay,
    openParlayId,
    onOpenParlayHandled,
    linkedSharedParlayId,
    onLinkedShareHandled,
  };
}
